import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Svg,
  Circle,
  Link,
} from "@react-pdf/renderer";
import {
  capitalizeExceptCommonWords,
  convertDaysToDuration,
  formatAddressString,
  formatDateToString,
  formatFees,
  getApplicantFullName,
} from "services";

// React-PDF CSS compatible
const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 12,
    fontFamily: "Helvetica",
  },
  header: {
    textAlign: "center",
    marginBottom: 20,
  },
  title: {
    fontSize: 18,
    textAlign: "center",
    marginBottom: 10,
    fontWeight: "bold",
  },
  institution: {
    textAlign: "right",
    marginBottom: 20,
  },
  applicantDetails: {
    marginTop: 10,
    marginBottom: 20,
  },
  section: {
    lineHeight: 1.2,
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 40,
    right: 40,
    textAlign: "center",
    fontSize: 10,
    color: "#888",
  },
  message: {
    lineHeight: 0.9,
    flexDirection: "column",
    gap: 10,
    marginBottom: 15,
  },
  // constants
  h4: {
    fontWeight: 600,
  },
  textBold: {
    fontWeight: 600,
  },
  marginYSm: {
    marginVertical: 5,
  },
  marginYMd: {
    marginVertical: 10,
  },
  // logo
  logoContainer: {
    width: "100%",
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: 10,
  },
  logo: {
    width: 25,
    height: 25,
    objectFit: "contain",
  },
  // table
  table: {
    width: "100%",
    fontSize: 12,
    lineHeight: 1.2,
    marginBottom: 10,
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 2,
  },
  tableColLabel: {
    width: "30%",
    fontWeight: "bold",
  },
  tableColValue: {
    width: "70%",
    paddingLeft: 10,
  },
  tableHeaderRow: {
    flexDirection: "row",
    // paddingVertical: 3,
    paddingBottom: 10,
  },
  // bullet list
  numberedList: {
    marginLeft: 10,
  },
  listItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 2,
    marginLeft: 20,
  },
  bullet: {
    marginTop: 5,
    marginRight: 6,
  },
  listText: {
    flex: 1,
  },
});

interface UnconditionalOfferLetterProps {
  data: any;
  conditions: string[];
  logoBase64: string | null;
}

// styling
const RowInfo = ({ label, value }: { label: string; value: any }) => {
  return (
    <View style={styles.tableRow}>
      <View style={styles.tableColLabel}>
        <Text>{label}</Text>
      </View>
      <View style={styles.tableColValue}>
        <Text>{value}</Text>
      </View>
    </View>
  );
};
const ListItem = ({ text }: { text: any }) => {
  return (
    <View style={styles.listItem}>
      <Svg width={4} height={4} style={styles.bullet}>
        <Circle cx="2" cy="2" r="2" fill="black" />
      </Svg>
      <Text style={styles.listText}>{text}</Text>
    </View>
  );
};
const renderCreditTransfer = (creditTransfer: any | null) => {
  if (!creditTransfer) {
    return null;
  }
  return (
    <View style={styles.message}>
      <View style={styles.numberedList}>
        <Text style={styles.h4}>Credit Transfer: {"\n"}</Text>
        <ListItem
          text={
            <>
              <Text style={styles.textBold}>Course: </Text>
              <Text>{`${creditTransfer.courseName} (${creditTransfer.courseCode})`}</Text>
            </>
          }
        />
        <ListItem
          text={
            <>
              <Text style={styles.textBold}>Institution: </Text>
              <Text>{creditTransfer.institutionName}</Text>
            </>
          }
        />
        <ListItem
          text={
            <>
              <Text style={styles.textBold}>Date Completed: </Text>
              <Text>{formatDateToString(creditTransfer.dateCompleted)}</Text>
            </>
          }
        />
      </View>
    </View>
  );
};

const UnconditionalOfferLetter: React.FC<UnconditionalOfferLetterProps> = ({
  data,
  logoBase64,
}) => {
  const basicDetails = data?.personalInformation?.basicDetails;
  const contactDetails = data?.personalInformation?.contactDetails;
  const educationDetails = data?.educationDetails;
  const courseDetails = data?.courseData;

  const date = new Date();
  const year = date.getFullYear();
  const lapseDate = new Date(date.setDate(date.getDate() + 14));

  const formattedInstitutionName = courseDetails.institutionName
    .replace(/\s*\([^)]*\)\s*/g, "")
    .trim();

  const formattedStudentAddress = () => {
    const a = contactDetails.homeAddress;
    if (!a) return "";

    return [
      a.street1,
      a.street2,
      [a.city, a.state, a.postalCode].filter(Boolean).join(", "),
      a.country,
    ]
      .filter(Boolean)
      .join("\n");
  };
  const estimatedAnnualFee =
    courseDetails.estimatedTotalCourseFee /
    (courseDetails.durationDay / 365.25);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.institution}>
          {logoBase64 && (
            <View style={styles.logoContainer}>
              <Image
                style={styles.logo}
                src={{ uri: logoBase64, method: "GET", headers: {}, body: "" }}
              />
            </View>
          )}
          <Text>{formattedInstitutionName}</Text>
          <Text>{capitalizeExceptCommonWords(formatAddressString(courseDetails.address[0]))}</Text>
          <Text>{courseDetails.country}</Text>
        </View>

        <View style={styles.applicantDetails}>
          <Text style={styles.marginYMd}>{formatDateToString(date)}</Text>
          <Text>{getApplicantFullName(basicDetails)}</Text>
          <Text>{formattedStudentAddress()}</Text>
        </View>

        <View style={styles.section}>
          <Text>
            Dear {basicDetails.title} {basicDetails.lastName},
          </Text>
          <Text style={styles.textBold}>
            Subject: Unconditional Offer of Admission
          </Text>
        </View>

        <View style={styles.message}>
          <Text style={styles.textBold}>
            Congratulations on your Full Offer of Admission!
          </Text>
          <Text>
            We are delighted to confirm that you have successfully met all the
            conditions outlined in your previous letter. It is with great
            pleasure that we provide you with a Full Offer of Admission to{" "}
            {formattedInstitutionName}.
          </Text>
          <Text>
            We are confident you will make a valuable contribution to our
            community and look forward to welcoming you.
          </Text>
          <Text>Your admission details are confirmed as follows:</Text>
        </View>

        {/* Unconditional offer table */}
        <View style={styles.table}>
          <View style={styles.tableHeaderRow}>
            <Text style={styles.textBold}>Program Details</Text>
          </View>

          <RowInfo label="Program Name:" value={courseDetails.courseName} />
          {courseDetails.courseCode && (
            <RowInfo label="Program Code:" value={courseDetails.courseCode} />
          )}
          <RowInfo label="CRICOS Code:" value={courseDetails.courseId} />
          <RowInfo
            label="Campus:"
            value={capitalizeExceptCommonWords(formatAddressString(courseDetails.address[0]))}
          />
          <RowInfo
            label="Mode of Study:"
            value={`${courseDetails.programType}, ${courseDetails.modeOfStudy}`}
          />
          <RowInfo
            label="Expected Duration:"
            value={convertDaysToDuration(courseDetails.durationDay)}
          />
          {courseDetails.startDate && (
            <>
              <RowInfo
                label="Commencement Date:"
                value={courseDetails.startDate ?? "N/A"}
              />
              <RowInfo label="Compulsory Orientation:" value="N/A" />
            </>
          )}
          <RowInfo
            label={`Indicative Annual Tuition Fee (${year}):`}
            value={`${formatFees(estimatedAnnualFee)}`}
          />
          <RowInfo
            label="Estimated Total Tuition Fee for Full Program:"
            value={
              <View>
                <Text>
                  {`${formatFees(courseDetails.estimatedTotalCourseFee)}\n`}
                  {"\n"}
                </Text>
                <Text style={{ fontSize: 11, fontStyle: "italic" }}>
                  Please note: This tuition fee is indicative and represents
                  tuition fees for students commencing in {year} only. Tuition
                  fees are reviewed annually and may be subject to increase for
                  each calendar year of your study.
                  {"\n"}
                  Refer to the university website for more information regarding
                  current rates.
                </Text>
              </View>
            }
          />
          <RowInfo label="Lapse Date:" value={formatDateToString(lapseDate)} />
          {!educationDetails?.isApplyingForCreditTransfer ? (
            <RowInfo
              label="Credit:"
              value="No credit has been awarded for prior studies. If you would
              like to apply for a credit transfer, please request for a form."
            />
          ) : (
            <View style={{ marginTop: 10 }}>
              <Text style={styles.h4}>Credit:{"\n"}</Text>
              <Text style={{ marginBottom: 10 }}>
                The following credit towards your course has been approved on
                the bases of your previous studies: {"\n"}
              </Text>
              {renderCreditTransfer(educationDetails?.creditTransfer1)}
              {renderCreditTransfer(educationDetails?.creditTransfer2)}
              {renderCreditTransfer(educationDetails?.creditTransfer3)}
              {renderCreditTransfer(educationDetails?.creditTransfer4)}
              {renderCreditTransfer(educationDetails?.creditTransfer5)}
              {renderCreditTransfer(educationDetails?.creditTransfer6)}
              {renderCreditTransfer(educationDetails?.creditTransfer7)}
            </View>
          )}
        </View>

        <View style={styles.message}>
          <Text style={styles.textBold}>How to Accept Your Offer</Text>
          <Text>
            This is the final step to secure your place. To accept this offer,
            you must complete the following steps by{" "}
            {`${formatDateToString(lapseDate)}`} or this offer will lapse.
          </Text>
          <View>
            <View style={styles.numberedList}>
              <>
                <Text style={styles.h4}>1. Review your Agreement: </Text>
                <Text>
                  Log in to your portal. Carefully read the Offer and Acceptance
                  Agreement. This is a legally binding document that contains:
                </Text>
              </>
              <ListItem text="Full terms and conditions of your enrolment." />
              <ListItem text="Our official Refund Policy." />
              <ListItem text="Your rights as an international student under the ESOS Framework." />
            </View>
            <View style={styles.numberedList}>
              <>
                <Text style={styles.h4}>2. Accept and Sign: </Text>
                <Text>
                  Digitally sign and submit the Offer and Acceptance Agreement
                  within the portal.
                </Text>
              </>
            </View>
            <View style={styles.numberedList}>
              <>
                <Text style={styles.h4}>3. Pay Your Deposit: </Text>
                <Text>
                  Make the required deposit payment to secure your place. This
                  payment will be credited toward your first semester's fees.
                </Text>
              </>
            </View>
          </View>
        </View>

        <View style={styles.message}>
          <Text style={styles.textBold}>Payment Options:</Text>
          <View>
            <ListItem
              text={
                <>
                  <Text style={styles.textBold}>
                    Online Portal (Recommended):
                  </Text>
                  Pay directly via credit card in your applicant portal.
                </>
              }
            />
            <ListItem
              text={
                <>
                  <Text style={styles.textBold}>
                    International Bank Transfer:
                  </Text>{" "}
                  Pay via our international payment partner (e.g., Flywire or
                  Convera) through your portal. This allows you to pay in your
                  local currency.
                </>
              }
            />
            <ListItem
              text={
                <>
                  <Text style={styles.textBold}>BPAY (in Australia):</Text>{" "}
                  [Biller Code and Reference Number]
                </>
              }
            />
          </View>
        </View>

        <View style={styles.message}>
          <Text style={styles.textBold}>
            Your Next Steps: Student Visa and Confirmation of Enrolment (CoE)
          </Text>
          <Text>
            Upon receipt of your digitally signed Acceptance Agreement and
            cleared deposit payment, {formattedInstitutionName} will issue your
            electronic Confirmation of Enrolment (CoE).
          </Text>
          <Text>
            The CoE is the official document you will need to apply for your
            Student visa (subclass 500). As part of your visa application, you
            will be required to answer a series of Genuine Student questions
            directly to the Department of Home Affairs. Our internal GS
            assessment is designed to prepare you for this, but the final
            decision on your visa rests with the Department.
          </Text>
          <Text>
            Please be aware that applying for your student visa is your
            responsibility, and we advise you to familiarise yourself with the
            process and requirements on their website:{" "}
            <Link href="https://immi.homeaffairs.gov.au/">
              Immigration and citizenship Website
            </Link>
            .
          </Text>
        </View>

        <View style={styles.message}>
          <Text style={styles.textBold}>
            Important Information for International Students
          </Text>
          <View>
            <ListItem
              text={
                <>
                  <Text style={styles.textBold}>ESOS Framework:</Text> As an
                  international student in Australia, your rights are protected
                  by the Education Services for Overseas Students (ESOS) Act
                  2000. You can learn more{" "}
                  <Link href="https://www.legislation.gov.au/C2004A00757/latest/text">
                    here
                  </Link>
                  .
                </>
              }
            />
            <ListItem
              text={
                <>
                  <Text style={styles.textBold}>Deferral:</Text> If you do not
                  meet your conditions by the deadline, you may be able to defer
                  your offer to the next available intake. Please contact us to
                  discuss your options.
                </>
              }
            />
          </View>
        </View>

        <View style={styles.message}>
          <Text>
            We congratulate you once again on this unconditional offer and are
            excited about the prospect of you joining us at{" "}
            {formattedInstitutionName}. If you have any questions regarding your
            offer or these conditions, please do not hesitate to contact the
            International Admissions Office.
          </Text>
        </View>

        <View style={styles.section}>
          <Text>Sincerely,</Text>
          <Text>Admissions Team</Text>
        </View>

        <Text style={styles.footer}>
          © 2025 EDUBAY Institute | Confidential
        </Text>
      </Page>
    </Document>
  );
};

export default UnconditionalOfferLetter;
